import {
  ActiveFiltersBar,
  RemovableFilterBadge,
} from "@/components/dashboard/active-filters-bar";
import type { BulkPaymentFeesFilters } from "@/hera-api/bulk-payment-api";
import {
  getLicenseTypesOptions,
  getRegistrantAccountUsersOptions,
  getRegistrantLicenseJurisdictionOptionsOptions,
} from "@/hera-api/client/@tanstack/react-query.gen";
import { useAuthQuerySetup } from "@/hooks/use-auth-query-setup";
import { jurisdictionOptionLabel } from "@/lib/jurisdiction-option-label";
import { useQuery } from "@tanstack/react-query";

/**
 * Shows which registrant dashboard filters the bulk payment screen inherited, so
 * a short property list is explained rather than looking like missing data.
 */
export function BulkPaymentDashboardFilterSummary({
  filters,
  onClearFilters,
}: {
  filters: Required<BulkPaymentFeesFilters>;
  onClearFilters: () => void;
}) {
  const { isAuthSetupComplete } = useAuthQuerySetup();

  const { data: jurisdictionOptionsData } = useQuery({
    ...getRegistrantLicenseJurisdictionOptionsOptions(),
    enabled: isAuthSetupComplete && filters.jurisdictionDisplayCodes.length > 0,
  });

  const { data: licenseTypesData } = useQuery({
    ...getLicenseTypesOptions(),
    enabled: isAuthSetupComplete && filters.licenseTypeIds.length > 0,
  });

  const { data: registrantUsersData } = useQuery({
    ...getRegistrantAccountUsersOptions(),
    enabled: isAuthSetupComplete && !!filters.registrantAccountId,
  });

  const chips: Array<{ key: string; label: string }> = [
    ...filters.jurisdictionDisplayCodes.map((code) => {
      const jurisdiction = (jurisdictionOptionsData?.jurisdictions ?? []).find(
        (j) => j.display_code === code,
      );
      return {
        key: `jurisdiction-${code}`,
        label: jurisdiction ? jurisdictionOptionLabel(jurisdiction) : code,
      };
    }),
    ...filters.licenseTypeIds.map((id) => ({
      key: `license-type-${id}`,
      label:
        (licenseTypesData?.license_types ?? []).find((type) => type.id === id)
          ?.name ?? id,
    })),
  ];

  if (filters.registrantAccountId) {
    const user = (registrantUsersData?.users ?? []).find(
      (u) => u.id === filters.registrantAccountId,
    );
    chips.push({
      key: `registrant-${filters.registrantAccountId}`,
      label: user?.name?.trim() || user?.email || filters.registrantAccountId,
    });
  }
  if (filters.amsCompanyName) {
    chips.push({
      key: `ams-${filters.amsCompanyName}`,
      label: filters.amsCompanyName,
    });
  }

  if (chips.length === 0) return null;

  return (
    <div className="space-y-1">
      <ActiveFiltersBar appliedCount={chips.length} onClearAll={onClearFilters}>
        {chips.map((chip) => (
          <RemovableFilterBadge key={chip.key} label={chip.label} />
        ))}
      </ActiveFiltersBar>
      <p className="text-xs text-muted-foreground">
        Showing only properties that match your dashboard filters. Clear them to
        pay every property with a balance due.
      </p>
    </div>
  );
}
